import type * as api from "./api";
import { coordinate } from "./location";

export const NAME_MAX = 60;
export const ADDRESS_MAX = 200;
export const CAPACITY_MAX = 20000;
export const FLOAT_MIN = 10;
export const FLOAT_MAX = 95;
export const FLOAT_DEFAULT = 75;
const TRUCK_MAX = 8;
const TRUCK = /^[A-Z0-9]{1,8}$/;
const CLOCK = /^\d{2}:\d{2}$/;

export const SENSOR_TEXT: Record<api.SensorType, string> = {
  none: "No sensor",
  float: "Float switch",
  level: "Level sensor",
};

/** Business rule S-1: a float switch reads `full` when the oil is above the float. */
export function sensorText(container: Pick<api.Container, "sensor_type" | "float_pct">): string {
  return container.sensor_type === "float" ? `Float at ${container.float_pct}%` : SENSOR_TEXT[container.sensor_type];
}

export function floatError(text: string): string | null {
  const value = Number(text.trim());
  const ok = text.trim() !== "" && Number.isInteger(value) && value >= FLOAT_MIN && value <= FLOAT_MAX;
  return ok ? null : `Enter the float height as a whole percent from ${FLOAT_MIN} to ${FLOAT_MAX}.`;
}

export interface Problem<F extends string> {
  field: F;
  text: string;
}

export interface DepotDraft {
  name: string;
  address: string;
  opens: string;
  closes: string;
}

export type DepotField = keyof DepotDraft;

const words = (text: string) => text.trim().split(/\s+/).filter(Boolean).join(" ");

export function depotDraft(depot: api.Depot | null, defaults: api.Fleet["defaults"]): DepotDraft {
  return {
    name: depot?.name ?? "",
    address: depot?.address ?? "",
    opens: depot?.opens ?? defaults.opens,
    closes: depot?.closes ?? defaults.closes,
  };
}

export function depotChange(draft: DepotDraft): api.DepotFields | Problem<DepotField> {
  const name = words(draft.name);
  const address = words(draft.address);
  if (!name || name.length > NAME_MAX) return { field: "name", text: `Give the depot a name of 1 to ${NAME_MAX} characters.` };
  if (address.length > ADDRESS_MAX) return { field: "address", text: `Give an address of at most ${ADDRESS_MAX} characters.` };
  if (!CLOCK.test(draft.opens)) return { field: "opens", text: "Enter the time the depot opens." };
  if (!CLOCK.test(draft.closes)) return { field: "closes", text: "Enter the time the depot closes." };
  if (draft.closes <= draft.opens) return { field: "closes", text: "The depot must close after it opens." };
  return { name, address, opens: draft.opens, closes: draft.closes };
}

export interface TruckDraft {
  code: string;
  hub_id: string;
  capacity: string;
  shift_start: string;
  shift_end: string;
  active: boolean;
}

export type TruckField = Exclude<keyof TruckDraft, "active">;

export function truckDraft(truck: api.Truck | null, found: Pick<api.Fleet, "defaults" | "depots">): TruckDraft {
  return {
    code: truck?.code ?? "",
    hub_id: truck?.hub_id ?? (found.depots.length === 1 ? found.depots[0].id : ""),
    capacity: truck ? String(truck.capacity_gal) : "",
    shift_start: truck?.shift_start ?? found.defaults.shift_start,
    shift_end: truck?.shift_end ?? found.defaults.shift_end,
    active: truck?.active ?? true,
  };
}

/** `taken` holds the codes of the other trucks of the fleet. */
export function truckChange(draft: TruckDraft, taken: readonly string[]): api.TruckFields | Problem<TruckField> {
  const code = draft.code.trim().toUpperCase();
  if (code.length > TRUCK_MAX) return { field: "code", text: `A truck code is 1 to ${TRUCK_MAX} letters and digits (now ${code.length}).` };
  if (!TRUCK.test(code)) return { field: "code", text: "Enter the truck code as 1 to 8 letters and digits, for example T2." };
  if (taken.includes(code)) return { field: "code", text: `Truck ${code} is in the fleet already.` };
  if (!draft.hub_id) return { field: "hub_id", text: "Choose the depot of the truck." };
  const capacity = Number(draft.capacity.replace(/,/g, "").trim());
  if (!draft.capacity.trim() || !Number.isFinite(capacity) || capacity <= 0 || capacity > CAPACITY_MAX) {
    return { field: "capacity", text: "Enter the capacity in gallons, from 1 to 20,000." };
  }
  if (!CLOCK.test(draft.shift_start)) return { field: "shift_start", text: "Enter the time the shift starts." };
  if (!CLOCK.test(draft.shift_end)) return { field: "shift_end", text: "Enter the time the shift ends." };
  if (draft.shift_end <= draft.shift_start) return { field: "shift_end", text: "The shift must end after it starts." };
  return {
    code,
    hub_id: draft.hub_id,
    capacity_gal: Math.round(capacity * 100) / 100,
    shift_start: draft.shift_start,
    shift_end: draft.shift_end,
    active: draft.active,
  };
}

export const hoursText = (from: string, to: string) => `${from}–${to}`;

/** The note after a depot save. A depot with no point is not used by the route planner (V-6). */
export function depotSavedText(name: string, changed: boolean, point: Pick<api.MapPoint, "source"> | null, notSent = false): string {
  const done = `${changed ? "Saved" : "Added"}: ${name}.`;
  const demo = " Demo data is not sent to Google, so";
  if (!point) return `${done}${notSent ? `${demo} it` : " It"} has no depot point yet. Press Change and place the point on the map.`;
  if (notSent) return `${done}${demo} the point did not change.`;
  return point.source === "geocode" ? `${done} The depot point comes from the address.` : done;
}

export const pointText = (depot: Pick<api.Depot, "lat" | "lng">) =>
  depot.lat === null || depot.lng === null ? "No point" : `${coordinate(depot.lat)}, ${coordinate(depot.lng)}`;

/** The size of a new route: the capacity of the fleet truck with this code, if there is one. */
export function fleetSize(trucks: readonly { code: string; capacity_gal: number }[], code: string): number | null {
  const found = trucks.find((truck) => truck.code === code.trim().toUpperCase());
  return found ? found.capacity_gal : null;
}

/** Rule V-11: the home areas of a truck, by code. */
export const homeAreasText = (truck: Pick<api.Truck, "home_areas">) =>
  truck.home_areas.length ? truck.home_areas.map((area) => area.code).join(", ") : "None";

export const truckSavedText = (code: string, changed: boolean, homeAreas: boolean) =>
  `${changed ? "Saved" : "Added"}: truck ${code}${homeAreas ? " and its home areas" : ""}.`;
